import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Title, Text, Stack, ScrollArea } from '@mantine/core';

import type { KeyValueItem } from '../../types/apiTypes';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import useOrgPermissions from '../../hooks/useOrgPermissions';
import { CenteredLoader, CompanyDetailsForm, type CompanyDetailsFormValues } from '../../components';
import { getAddressFromString, addressToString } from '../../utils/addressUtils';
import { DefaultKeys, keyValueListToObj } from '../../utils/keyValueUtils';

const CompanyDetails: React.FC = () => {
  const { organization } = useParams();
  const { userHasPermission } = useOrgPermissions();

  const [
    initialFormValues,
    setInitialFormValues
  ] = useState<CompanyDetailsFormValues | undefined>(undefined);

  const orgKeyValueQueryRoute = `/app/organizations/${organization}/key-value-items` as const;
  const { data: orgData, isLoading: orgDataLoading } = useEnsisQuery(
    `/app/organizations/${organization}/data`
  );

  const onGetOrgKeyValueDataSuccess = useCallback(
    (keyValuePairs: KeyValueItem[]) => {
      const keyValuesObj = keyValueListToObj(keyValuePairs);
      const parsedAddress = keyValuesObj[DefaultKeys.ADDRESS] === undefined
        ? undefined
        : getAddressFromString(keyValuesObj[DefaultKeys.ADDRESS]);
      setInitialFormValues({
        address1: parsedAddress?.address1 ?? '',
        address2: parsedAddress?.address2 ?? '',
        city: parsedAddress?.city ?? '',
        state: parsedAddress?.state ?? '',
        zipCode: parsedAddress?.zipCode ?? '',
        phone: keyValuesObj[DefaultKeys.PHONE] ?? '',
        ein: keyValuesObj[DefaultKeys.EIN] ?? '',
        authorizedRep: keyValuesObj[DefaultKeys.AUTHORIZED_REP] ?? '',
        email: keyValuesObj[DefaultKeys.COMPANY_EMAIL] ?? '',
        name: orgData?.name ?? ''
      });
    },
    [orgData?.name]
  );

  const { isLoading: keyValuesLoading } = useEnsisQuery(
    orgKeyValueQueryRoute,
    { onSuccess: (responseData) => { onGetOrgKeyValueDataSuccess(responseData?.items); } }
  );

  const editOrgKeyValueItemsMutation = useEnsisMutation(
    orgKeyValueQueryRoute,
    {
      contentType: 'application/json',
      requestType: 'patch',
      successMessage: 'Organization info updated',
      queryKeysToInvalidate: [orgKeyValueQueryRoute],
      onSuccess: onGetOrgKeyValueDataSuccess
    }
  );

  const handleSubmit = useCallback((values: CompanyDetailsFormValues) => {
    const { phone, email, ein, authorizedRep, ...addressValues } = values;
    editOrgKeyValueItemsMutation.mutate({
      items: [
        {
          key: DefaultKeys.PHONE,
          value: phone
        },
        {
          key: DefaultKeys.ADDRESS,
          value: addressToString(addressValues)
        },
        {
          key: DefaultKeys.COMPANY_NAME,
          value: orgData?.name ?? ''
        },
        {
          key: DefaultKeys.EIN,
          value: ein
        },
        {
          key: DefaultKeys.COMPANY_EMAIL,
          value: email
        },
        {
          key: DefaultKeys.AUTHORIZED_REP,
          value: authorizedRep
        }
      ]
    });
  }, [orgData?.name]);

  const loading = keyValuesLoading || orgDataLoading;

  if (loading) {
    return <CenteredLoader />;
  }

  const canEdit = userHasPermission('edit_key_value_items');

  return (
    <ScrollArea type='hover' flex={1}>
      <Stack>
        <Box>
          <Title order={6}>
            Basic Information
          </Title>
          {!canEdit && <Text>  Contact your company admin to request changes to this information </Text>}
        </Box>
        <CompanyDetailsForm
          onSubmit={handleSubmit}
          canEdit={canEdit}
          initialValues={initialFormValues}
          lockedFields={['name']}
        />
      </Stack>
    </ScrollArea>
  );
};

export default CompanyDetails;
